import React from 'react';
import * as Sentry from '@sentry/react';

const CHUNK_ERROR_RELOAD_KEY = 'chunk_error_reload';

// The shell remounts on every load, including loads that later fail, so a
// boolean flag cleared on mount can never stop a second reload. Timestamp it.
const RELOAD_COOLDOWN_MS = 10000;

// Webpack threw ChunkLoadError; Vite throws a plain TypeError whose message
// differs per browser (Chrome / Firefox / Safari).
const CHUNK_ERROR_MESSAGES = [
  /Loading chunk .* failed/,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
];

function canReload() {
  const lastReload = Number(sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY));

  return !lastReload || Date.now() - lastReload > RELOAD_COOLDOWN_MS;
}

function isChunkLoadError(error) {
  if (!error) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  const message = error.message || '';

  return CHUNK_ERROR_MESSAGES.some((pattern) => pattern.test(message));
}

export class ErrorBoundary extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error) && canReload()) {
      sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, String(Date.now()));
      Sentry.captureException(error, {
        tags: { chunk_error_reload: true },
      });
      window.location.reload();
      return;
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.errorInfo) {
      // Error path
      return (
        <div style={{ maxWidth: '720px' }}>
          <h1>Something went wrong.</h1>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }
    // Normally, just render children
    return this.props.children;
  }
}
